import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch } from '../../nexus/nexusClient';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { mailSecurityListFixture, mailSecurityDetailFixture, mailSecurityPreviewFixture } from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-mail-security-overview.md
 * Endpoints: /v1/admin/mail-security
 */

/** GET /v1/admin/mail-security?domain=&tenant_id= */
export function useMailSecurityList({ domainFilter = '', tenantId = '' } = {}) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-mail-security', { domainFilter, tenantId }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      const res = fixturesMode
        ? (await fixtureDelay(), mailSecurityListFixture({ domainFilter, tenantId }))
        : await (async () => {
            const qs = new URLSearchParams();
            if (domainFilter) qs.set('domain', domainFilter);
            if (tenantId) qs.set('tenant_id', tenantId);
            return nexusFetch(`/v1/admin/mail-security?${qs.toString()}`);
          })();
      const raw = res?.records ?? res?.items ?? (Array.isArray(res) ? res : []);
      const list = raw.map(normalizeMailSecurityRow);
      return { records: list, raw: res };
    },
    staleTime: 30_000,
  });
}

/**
 * Normalisiert eine einzelne mail_security_record-Row aus Nexus auf die
 * historische UI-Form. Wird sowohl auf Live- als auch Fixture-Pfaden
 * angewendet damit die UI nie zwischen beiden Modi divergiert.
 */
function normalizeMailSecurityRow(r) {
  return {
    ...r,
    account: r.account ?? r.account_id ?? null,
    bimi_active: r.bimi_active ?? !!(r.bimi ?? r.bimi_logo_url),
    bimi_logo_bytes: r.bimi_logo_bytes ?? r.bimi_bytes ?? null,
    dkim_algo: r.dkim_algo ?? r.algorithm ?? null,
  };
}

/**
 * GET /v1/admin/mail-security/:domain — Nexus liefert
 *   { record: {...mail_security_records row + parsed state},
 *     dkim_history: [...mail_dkim_keys] }
 * Wir mergen `dkim_history` in den Record, weil die Detail-View es als
 * `record.dkim_history` erwartet. Außerdem normalisieren wir Feldnamen
 * (`bimi_bytes` ⇄ `bimi_logo_bytes`, `algorithm` ⇄ `algo`) damit die UI
 * mit Live- und Fixture-Daten konsistent rendert.
 */
export function useMailSecurityDetail(domain) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-mail-security-detail', domain, fixturesMode ? 'fixture' : 'live'],
    enabled: !!domain,
    queryFn: async () => {
      const res = fixturesMode
        ? (await fixtureDelay(), mailSecurityDetailFixture(domain))
        : await nexusFetch(`/v1/admin/mail-security/${encodeURIComponent(domain)}`);
      if (!res) return null;
      const rec = res.record ?? res;
      const history = (res.dkim_history ?? rec.dkim_history ?? []).map((h) => ({
        selector: h.selector,
        algo: h.algorithm ?? h.algo,
        created_at: h.created_at,
        rotated_at: h.rotated_at,
        status: h.active === 1 || h.active === true || h.status === 'active'
          ? 'active'
          : (h.status ?? (h.rotated_at ? `rotated_at_${String(h.rotated_at).slice(0,10)}` : 'inactive')),
      }));
      return {
        ...normalizeMailSecurityRow(rec),
        dkim_algo: rec.dkim_algo ?? rec.algorithm ?? history.find((h) => h.status === 'active')?.algo ?? null,
        dkim_history: history,
      };
    },
  });
}

/** POST /v1/admin/mail-security/:domain/dkim/rotate */
export function useRotateDkim() {
  return useApiMutation({
    live: (_fetch, { domain, algo }) =>
      nexusFetch(`/v1/admin/mail-security/${encodeURIComponent(domain)}/dkim/rotate`, {
        method: 'POST',
        body: algo ? { algo } : {},
      }),
    fixture: ({ domain }) => ({ ok: true, domain, new_selector: `mail${Date.now()}` }),
    invalidate: ['nexus-mail-security', 'nexus-mail-security-detail'],
    successMessage: (res, { domain }) =>
      res?.new_selector
        ? `DKIM rotiert · neuer Selector: ${res.new_selector} (${domain})`
        : `DKIM-Rotation für ${domain} angestoßen.`,
  });
}

/**
 * GET /v1/admin/mail-security/:domain/preview — Read-only Diff vor Reapply.
 *
 * Nexus liefert:
 *   { state, dns_target, dns_status, records, desired,
 *     operations: [{ action: 'create'|'update'|'delete', kind: 'TXT',
 *                    name, value, ttl, id? }],
 *     suggestions: [{ name, level: 'ok'|'drift'|'info', message }] }
 *
 * Die Detail-View erwartet historisch:
 *   suggestions: [{ record, state, from, to, message }]
 *   operations:  string[]   (für eine kompakte Inline-Anzeige)
 *
 * Wir mappen hier, damit die UI-Komponenten unverändert bleiben und
 * Fixture-/Live-Mode dieselbe Form sehen.
 */
const SUGGESTION_STATE = { ok: 'ok', drift: 'drift', info: 'missing' };

function normalisePreview(res) {
  if (!res || typeof res !== 'object') return res;
  const ops = Array.isArray(res.operations) ? res.operations : [];
  const opsLabels = ops.map((o) => {
    if (typeof o === 'string') return o;
    return `${o.action ?? 'op'}_${o.name ?? o.kind ?? 'record'}`;
  });
  const suggestions = (res.suggestions ?? []).map((s) => {
    if (s && typeof s === 'object' && (s.record || s.state)) return s;
    const op = ops.find((o) => o?.name === s?.name);
    return {
      record: s.name ?? s.record ?? '?',
      state: SUGGESTION_STATE[s.level] ?? s.state ?? s.level ?? 'unknown',
      message: s.message ?? null,
      // Bei drift hängt Nexus alten/neuen Wert in den Message-String —
      // wir geben ihn 1:1 weiter und lassen `from/to` undefined, die UI
      // rendert dann nur die Message statt der from→to-Diff-Zeile.
      from: undefined,
      to: op?.value,
    };
  });
  return {
    ...res,
    operations: opsLabels,
    operations_raw: ops,        // wer den vollen Op-Detail braucht (Modal …)
    suggestions,
  };
}

export function useMailSecurityPreview(domain) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-mail-security-preview', domain, fixturesMode ? 'fixture' : 'live'],
    enabled: !!domain,
    queryFn: async () => {
      const res = fixturesMode
        ? (await fixtureDelay(), mailSecurityPreviewFixture(domain))
        : await nexusFetch(`/v1/admin/mail-security/${encodeURIComponent(domain)}/preview`);
      return normalisePreview(res);
    },
    staleTime: 10_000,
  });
}

/**
 * POST /v1/admin/mail-security/:domain/bimi-logo — Multipart-Upload.
 * Field-Name `logo`. Backend normalisiert zu SVG-Tiny 1.2.
 * Wir setzen Content-Type NICHT explizit — der Browser hängt die
 * multipart-boundary an.
 */
export function useUploadBimiLogo() {
  return useApiMutation({
    live: (_fetch, { domain, file }) => {
      const fd = new FormData();
      fd.append('logo', file);
      return nexusFetch(`/v1/admin/mail-security/${encodeURIComponent(domain)}/bimi-logo`, {
        method: 'POST',
        body: fd,
      });
    },
    fixture: ({ domain, file }) => ({
      ok: true,
      bimi_logo_url: `https://bimi.valitype.com/${domain}/${file?.name ?? 'logo.svg'}`,
      bytes: file?.size ?? 0,
      hint: 'Normalisiert zu SVG-Tiny 1.2',
    }),
    invalidate: ['nexus-mail-security', 'nexus-mail-security-detail'],
    successMessage: (res) =>
      res?.hint ? `BIMI-Logo gespeichert · ${res.hint}` : 'BIMI-Logo gespeichert.',
  });
}

/** PUT /v1/admin/mail-security/:domain/vmc — `{ vmc_url: <url|null> }` */
export function useSetVmc() {
  return useApiMutation({
    live: (_fetch, { domain, vmc_url }) =>
      nexusFetch(`/v1/admin/mail-security/${encodeURIComponent(domain)}/vmc`, {
        method: 'PUT',
        body: { vmc_url },
      }),
    fixture: ({ domain, vmc_url }) => ({ ok: true, vmc_url, warnings: [] }),
    invalidate: ['nexus-mail-security', 'nexus-mail-security-detail'],
    successMessage: (res, { vmc_url }) => {
      if (!vmc_url) return 'VMC entfernt.';
      if (Array.isArray(res?.warnings) && res.warnings.length > 0) {
        return `VMC gesetzt · Warnungen: ${res.warnings.join('; ')}`;
      }
      return 'VMC validiert und gesetzt.';
    },
  });
}

/** POST /v1/admin/mail-security/:domain/reapply — `{ account_id, primary_ip }` */
export function useReapplyMailSecurity() {
  return useApiMutation({
    live: (_fetch, { domain, account_id, primary_ip }) =>
      nexusFetch(`/v1/admin/mail-security/${encodeURIComponent(domain)}/reapply`, {
        method: 'POST',
        body: { account_id, primary_ip },
      }),
    fixture: ({ domain }) => ({ ok: true, domain, job_id: `apply-${Date.now()}` }),
    invalidate: ['nexus-mail-security', 'nexus-mail-security-detail'],
    successMessage: (res, { domain }) =>
      res?.job_id
        ? `Re-Apply für ${domain} enqueued (Job ${res.job_id}).`
        : `Re-Apply für ${domain} angestoßen.`,
  });
}
